/**
 * PatientOwnerDialog — Componente unificado para crear/editar paciente + propietario.
 * Schema canónico:
 *   owners: { name, rut, celular, mail, contact, region, comuna, convenio }
 *   patients: { name, species, breed, genero, birthDate, weight, ownerId }
 *
 * Props:
 *   open         {boolean}
 *   onClose      {function}
 *   onSaved      {function(enrichedPatient)}
 *   initial      {object|null} – datos para modo edición
 *   showConvenio {boolean} – default true; pasar false para ocultar en contextos públicos
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Divider,
  MenuItem, CircularProgress, Alert, InputAdornment, Autocomplete,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { collection, addDoc, updateDoc, doc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { REGIONES_COMUNAS } from '../utils/regionData';
import { normalizeRut } from '../utils/validators';
import { useBranch } from '../context/BranchContext';
import SearchableSelect from './SearchableSelect';

const emptyOwner   = () => ({ name: '', rut: '', celular: '', mail: '', region: '', comuna: '', convenio: '' });
const emptyPatient = () => ({ name: '', species: '', breed: '', genero: '', birthDate: '', weight: '' });

export default function PatientOwnerDialog({ open, onClose, onSaved, initial = null, showConvenio = true }) {
  const isEdit = !!initial?.patientId;

  const [owner,    setOwner]    = useState(emptyOwner());
  const [patient,  setPatient]  = useState(emptyPatient());
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [ownerFound, setOwnerFound] = useState(false);
  const [existingOwnerId, setExistingOwnerId] = useState(null);
  const [breeds,   setBreeds]   = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [patientBranches, setPatientBranches] = useState([]);
  const { branches, accessibleBranches, activeBranchId, isAllBranches } = useBranch();

  useEffect(() => {
    getDocs(collection(db, 'breeds'))
      .then(s => setBreeds(s.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
    getDocs(collection(db, 'convenios'))
      .then(s => setConvenios(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.isActive !== false)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    setError('');
    setOwnerFound(false);
    setExistingOwnerId(null);
    if (isEdit) {
      setOwner({
        name:     initial.ownerName     || '',
        rut:      initial.ownerRut      || '',
        celular:  initial.ownerCelular  || '',
        mail:     initial.ownerMail || initial.ownerContact || '',
        region:   initial.ownerRegion   || '',
        comuna:   initial.ownerComuna   || '',
        convenio: initial.ownerConvenio || '',
      });
      setPatient({
        name:      initial.name      || '',
        species:   initial.species   || '',
        breed:     initial.breed     || '',
        genero:    initial.genero    || '',
        birthDate: initial.birthDate || '',
        weight:    initial.weight != null ? String(initial.weight) : '',
      });
      setPatientBranches(branches.filter((b) => (initial.branchIds || []).includes(b.id)));
      setExistingOwnerId(initial.ownerId || null);
    } else {
      setOwner(emptyOwner());
      setPatient(emptyPatient());
      // Precargar la sucursal activa al crear (si no es "Todas")
      setPatientBranches(!isAllBranches ? branches.filter((b) => b.id === activeBranchId) : []);
    }
  }, [open, isEdit, initial, branches, activeBranchId, isAllBranches]);

  const o = (f) => (e) => setOwner(prev => ({ ...prev, [f]: e.target.value }));
  const p = (f) => (e) => setPatient(prev => ({ ...prev, [f]: e.target.value }));

  const handleSpeciesChange = (e) => {
    setPatient(prev => ({ ...prev, species: e.target.value, breed: '' }));
  };

  const handleRegionChange = (e) => {
    setOwner(prev => ({ ...prev, region: e.target.value, comuna: '' }));
  };

  const filteredBreeds = patient.species
    ? breeds.filter(b => b.species === patient.species).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    : [];

  const comunasOptions = owner.region ? REGIONES_COMUNAS[owner.region] || [] : [];

  const handleRutSearch = async () => {
    const r = normalizeRut(owner.rut);
    if (!r) return;
    setError('');
    try {
      const snap = await getDocs(query(collection(db, 'owners'), where('rut', '==', r)));
      if (!snap.empty) {
        const d = snap.docs[0];
        const o = d.data();
        setOwner({
          name:     o.name     || '',
          rut:      o.rut      || '',
          celular:  o.celular  || '',
          mail:     o.mail || o.contact || '',
          region:   o.region   || '',
          comuna:   o.comuna   || '',
          convenio: o.convenio || '',
        });
        setExistingOwnerId(d.id);
        setOwnerFound(true);
      } else {
        setOwnerFound(false);
        setExistingOwnerId(null);
        setError('Tutor no encontrado con ese RUT.');
      }
    } catch {
      setError('Error al buscar tutor.');
    }
  };

  const handleCelularSearch = async () => {
    const phone = (owner.celular || '').replace(/\D/g, '');
    if (!phone) return;
    setError('');
    try {
      const snap = await getDocs(query(collection(db, 'owners'), where('celular', '==', phone)));
      if (!snap.empty) {
        const d = snap.docs[0];
        const o = d.data();
        setOwner({
          name:     o.name     || '',
          rut:      o.rut      || '',
          celular:  o.celular  || '',
          mail:     o.mail || o.contact || '',
          region:   o.region   || '',
          comuna:   o.comuna   || '',
          convenio: o.convenio || '',
        });
        setExistingOwnerId(d.id);
        setOwnerFound(true);
      } else {
        setOwnerFound(false);
        setExistingOwnerId(null);
        setError('Tutor no encontrado con ese celular.');
      }
    } catch {
      setError('Error al buscar tutor.');
    }
  };

  const clearOwnerFound = () => {
    setOwnerFound(false);
    setExistingOwnerId(null);
    setError('');
  };

  const handleSave = async () => {
    if (!patient.name.trim() || !owner.name.trim()) {
      setError('Nombre del paciente y del propietario son obligatorios.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const ownerRut = normalizeRut(owner.rut);
      const ownerPayload = {
        name:     owner.name.trim(),
        rut:      ownerRut || owner.rut.trim(),
        celular:  owner.celular.trim(),
        mail:     owner.mail.trim(),
        contact:  owner.mail.trim(),
        region:   owner.region,
        comuna:   owner.comuna,
        convenio: owner.convenio,
      };
      const patientPayload = {
        name:      patient.name.trim(),
        species:   patient.species,
        breed:     patient.breed,
        genero:    patient.genero,
        birthDate: patient.birthDate,
        weight:    patient.weight !== '' ? parseFloat(patient.weight) : null,
        branchIds: patientBranches.map((b) => b.id),
      };

      if (isEdit) {
        const ownerRut = normalizeRut(owner.rut);
        const initialRut = normalizeRut(initial.ownerRut || '');
        const phone = (owner.celular || '').replace(/\D/g, '');
        const initialPhone = (initial.ownerCelular || '').replace(/\D/g, '');
        const ownerChanged = ownerRut !== initialRut || (phone !== initialPhone);

        let finalOwnerId = initial.ownerId || null;

        if (ownerChanged) {
          let ownerRef = null;
          if (ownerRut) {
            const existingByRut = await getDocs(query(collection(db, 'owners'), where('rut', '==', ownerRut)));
            if (!existingByRut.empty) {
              const found = existingByRut.docs[0];
              if (found.id !== initial.ownerId) ownerRef = found.ref;
            }
          }
          if (!ownerRef && phone) {
            const existingByPhone = await getDocs(query(collection(db, 'owners'), where('celular', '==', phone)));
            if (!existingByPhone.empty) {
              const found = existingByPhone.docs[0];
              if (found.id !== initial.ownerId) ownerRef = found.ref;
            }
          }
          if (ownerRef) {
            finalOwnerId = ownerRef.id;
            await updateDoc(ownerRef, ownerPayload);
          } else {
            const newOwner = await addDoc(collection(db, 'owners'), ownerPayload);
            finalOwnerId = newOwner.id;
          }
        } else if (finalOwnerId) {
          // El RUT/celular no cambiaron, pero hay que guardar cambios del owner (convenio, etc.)
          await updateDoc(doc(db, 'owners', finalOwnerId), ownerPayload);
        }

        await updateDoc(doc(db, 'patients', initial.patientId), { ...patientPayload, ownerId: finalOwnerId });
        onSaved({ patientId: initial.patientId, ownerId: finalOwnerId, ...patientPayload, ownerName: ownerPayload.name, ownerMail: ownerPayload.mail });
      } else {
        let ownerRef;
        if (existingOwnerId) {
          ownerRef = doc(db, 'owners', existingOwnerId);
          await updateDoc(ownerRef, ownerPayload);
        } else if (ownerRut) {
          const existing = await getDocs(query(collection(db, 'owners'), where('rut', '==', ownerRut)));
          if (!existing.empty) {
            ownerRef = existing.docs[0].ref;
            await updateDoc(ownerRef, ownerPayload);
          }
        }
        if (!ownerRef) {
          ownerRef = await addDoc(collection(db, 'owners'), ownerPayload);
        }
        const patientRef = await addDoc(collection(db, 'patients'), { ...patientPayload, ownerId: ownerRef.id });
        const parts = ownerPayload.name.split(' ');
        const ownerApellido = parts.length > 1 ? parts.slice(1).join(' ') : ownerPayload.name;
        onSaved({
          id:           patientRef.id,
          ownerId:      ownerRef.id,
          ...patientPayload,
          ownerName:    ownerPayload.name,
          ownerApellido,
          ownerMail:    ownerPayload.mail,
        });
      }
      onClose();
    } catch {
      setError('Error al guardar. Intenta nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{isEdit ? 'Editar Paciente' : 'Nuevo Paciente'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Propietario ── */}
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary' }}>
          DATOS DEL PROPIETARIO
        </Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" label="Nombre completo *"
              value={owner.name} onChange={o('name')} disabled={ownerFound} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" label="RUT" placeholder="12345678-9"
              value={owner.rut}
              onChange={(e) => { clearOwnerFound(); setOwner((prev) => ({ ...prev, rut: e.target.value })); }}
              onKeyDown={(e) => e.key === 'Enter' && handleRutSearch()}
              disabled={ownerFound}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleRutSearch} edge="end" disabled={!owner.rut}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" label="Celular *"
              value={owner.celular}
              onChange={(e) => { clearOwnerFound(); setOwner((prev) => ({ ...prev, celular: e.target.value })); }}
              onKeyDown={(e) => e.key === 'Enter' && handleCelularSearch()}
              disabled={ownerFound}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCelularSearch} edge="end" disabled={!owner.celular}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" label="Email" type="email"
              value={owner.mail} onChange={o('mail')} disabled={ownerFound} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" select label="Región"
              value={owner.region} onChange={handleRegionChange} disabled={ownerFound}>
              <MenuItem value="">Seleccionar región...</MenuItem>
              {Object.keys(REGIONES_COMUNAS).map(r => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <SearchableSelect fullWidth size="small" label="Comuna"
              value={owner.comuna} disabled={!owner.region || ownerFound}
              onChange={(e) => o('comuna')({ target: { value: e.target.value } })}
              options={comunasOptions.map(c => ({ value: c, label: c }))}
              emptyLabel="Seleccionar comuna..." />
          </Grid>
          {showConvenio && (
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <SearchableSelect fullWidth size="small" label="Convenio"
                value={owner.convenio} disabled={ownerFound}
                onChange={(e) => o('convenio')({ target: { value: e.target.value } })}
                options={convenios.map(c => ({ value: c.name, label: c.name }))}
                emptyLabel="Sin convenio"
                helperText={convenios.length === 0 ? 'Sin convenios configurados' : undefined} />
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* ── Paciente ── */}
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary' }}>
          DATOS DEL PACIENTE
        </Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" label="Nombre *"
              value={patient.name} onChange={p('name')} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" select label="Especie"
              value={patient.species} onChange={handleSpeciesChange}>
              <MenuItem value="">Seleccionar...</MenuItem>
              <MenuItem value="Canino">Canino</MenuItem>
              <MenuItem value="Felino">Felino</MenuItem>
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <SearchableSelect fullWidth size="small" label="Raza"
              value={patient.breed} disabled={!patient.species}
              onChange={(e) => p('breed')({ target: { value: e.target.value } })}
              options={filteredBreeds.map(b => ({ value: b.name, label: b.name }))}
              emptyLabel="Seleccionar raza..."
              helperText={
                !patient.species ? 'Selecciona primero una especie'
                : filteredBreeds.length === 0 ? 'Sin razas cargadas'
                : `${filteredBreeds.length} razas disponibles`
              } />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" select label="Sexo"
              value={patient.genero} onChange={p('genero')}>
              <MenuItem value="">Sin especificar</MenuItem>
              <MenuItem value="Macho">Macho</MenuItem>
              <MenuItem value="Hembra">Hembra</MenuItem>
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" label="Fecha de Nacimiento" type="date"
              InputLabelProps={{ shrink: true }}
              value={patient.birthDate} onChange={p('birthDate')} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" label="Peso" type="number"
              InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
              inputProps={{ min: 0, step: 0.1 }}
              value={patient.weight} onChange={p('weight')} />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              multiple
              size="small"
              options={accessibleBranches}
              value={patientBranches}
              onChange={(e, value) => setPatientBranches(value)}
              getOptionLabel={(b) => b.name}
              isOptionEqualToValue={(o2, v) => o2.id === v.id}
              renderInput={(params) => (
                <TextField {...params} label="Sucursal(es)" placeholder="Selecciona sucursal(es)" />
              )}
              noOptionsText="Sin sucursales configuradas"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : (isEdit ? 'Guardar cambios' : 'Registrar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
